import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { catchError, lastValueFrom, Subject, takeUntil, throwError } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { ViewMode } from '@src/models';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { AlertService } from '@src/core/services';
import { PhotoService } from '@src/app/modules/photo';

import { LoyaltyProgramCollectionsService } from '../services/loyalty-program-collections.service';
import { LoyaltyProgramPermService } from '../../services';
import { LoyaltyProgramCollection } from '../types';
import { LpCollectionsInfoStoreService } from '../services';

@Component({
  selector: 'lp-collection-info',
  templateUrl: './lp-collection-info.component.html',
  styleUrls: ['./lp-collection-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpCollectionInfoComponent implements OnInit, OnChanges {
  private readonly loyaltyProgramCollectionsService = inject(LoyaltyProgramCollectionsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly permService = inject(LoyaltyProgramPermService);
  private readonly translateService = inject(TranslateService);
  private readonly alertService = inject(AlertService);
  private readonly photoService = inject(PhotoService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly lpCollectionsInfoStore = inject(LpCollectionsInfoStoreService);

  @Input() mode: ViewMode = 'view';
  @Input() id?: string;
  @Input() unionId?: string;
  @Input() ref?: string;

  @Output() cancelEditing = new EventEmitter<void>();
  @Output() saved = new EventEmitter<string>();
  @Output() deleted = new EventEmitter<void>();
  @Output() onClickEditButton = new EventEmitter<void>();

  data?: LoyaltyProgramCollection;

  allowEditing$ = this.permService.allowCollectionEditing$;
  allowDeleting$ = this.permService.allowCollectionDeleting$;
  allowedCollectionStatisticsViewing = this.permService.allowedCollectionStatisticsViewing;

  loading = false;

  private readonly confirmDeleteDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.deleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  private readonly confirmSaveDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.saveHeader'),
      size: 's',
      closeable: false,
    },
  );

  private destroyed$$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.lpCollectionsInfoStore.data$.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      if (!this.id) return;

      this.data = this.lpCollectionsInfoStore.getData(this.id);
      this.loading = !this.data;

      if (this.data) {
        this.permService.loadCollectionPermissions(this.data.parentOrganisationId);
      }

      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.id) {
      if (this.id && this.data?.id !== this.id) {
        this.loadCollectionData(this.id);
      }
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.data = undefined;
        this.cdr.markForCheck();
      }
    }
  }

  onClickDeleteButton() {
    this.confirmDeleteDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        const collectionId = this.data?.id;

        if (res && collectionId) {
          this.loyaltyProgramCollectionsService
            .deleteCollection(collectionId)
            .pipe(takeUntil(this.destroyed$$))
            .subscribe(() => {
              this.alertService.success(
                this.translateService.instant('components.lpCollectionInfo.alerts.successes.deleteCollection'),
              );
              this.deleted.emit();
            });
        }
      },
    });
  }

  async onClickSaveButton(collection: LoyaltyProgramCollection) {
    this.loading = true;

    this.confirmSaveDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: async res => {
        if (res) {
          if (collection.bannerBackground) {
            collection.bannerBackgroundPhotoId = (
              await lastValueFrom(this.photoService.uploadPhoto(collection.bannerBackground))
            ).files?.[0].id;
          }

          if (this.mode === 'create') {
            this.loyaltyProgramCollectionsService
              .createCollection(collection)
              .pipe(
                catchError(err => {
                  this.loading = false;
                  this.cdr.markForCheck();

                  return throwError(() => err);
                }),
                takeUntil(this.destroyed$$),
              )
              .subscribe(res => {
                if (res.id) {
                  this.saved.emit(res.id);
                }

                this.loading = false;
                this.cdr.markForCheck();
              });
          }

          if (this.mode === 'edit') {
            this.loyaltyProgramCollectionsService
              .editCollection(collection)
              .pipe(
                catchError(err => {
                  this.loading = false;
                  this.cdr.markForCheck();

                  return throwError(() => err);
                }),
                takeUntil(this.destroyed$$),
              )
              .subscribe(res => {
                if (res.id) {
                  this.loadCollectionData(res.id);
                  this.saved.emit(res.id);
                }

                this.loading = false;
                this.cdr.markForCheck();
              });
          }
        } else {
          this.loading = false;
          this.cdr.markForCheck();
        }
      },
    });
  }

  onClickCancelButton() {
    this.cancelEditing.emit();
  }

  onCheckPush() {
    if (!this.id) {
      // TODO: вывести ошибку
      return;
    }

    this.loyaltyProgramCollectionsService
      .checkPush(this.id)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(() => {
        this.alertService.success(
          this.translateService.instant('components.lpCollectionInfo.alerts.successes.pushSent'),
        );
      });
  }

  private loadCollectionData(id: string) {
    this.id = id;

    this.loyaltyProgramCollectionsService
      .getCollectionById(id, this.unionId, this.ref)
      .pipe(
        catchError(err => {
          this.data = undefined;
          this.loading = false;
          this.cdr.markForCheck();

          return throwError(() => err);
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe();
  }
}
